import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  type Firestore,
  type FirestoreDataConverter,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  BLOCK_SUBTYPE_LIMIT,
  BlockSubtypeValidationError,
  normalizeBlockSubtypeInput,
  type BlockSubtype,
  type BlockSubtypeInput,
} from '@modules/shared-types';
import { blockSubtypeConverter } from '../converters/blockSubtypeConverter';
import { CoreFirebaseService } from './CoreFirebaseService';

export class BlockSubtypeServiceError extends Error {
  constructor(readonly code: 'limit_exceeded' | 'duplicate_slug' | 'not_found', message: string) {
    super(message);
    this.name = 'BlockSubtypeServiceError';
  }
}

/** Client-SDK owner of projects/{projectId}/blockSubtypes. */
export class BlockSubtypeService {
  private readonly firestore: Firestore;

  constructor(
    private readonly projectId: string,
    private readonly converter: FirestoreDataConverter<BlockSubtype> = blockSubtypeConverter,
    firestore?: Firestore,
  ) {
    this.firestore = firestore ?? CoreFirebaseService.getInstance().firestore;
  }

  private get collectionPath(): string {
    return `projects/${this.projectId}/blockSubtypes`;
  }

  async list(options: { enabledOnly?: boolean } = {}): Promise<BlockSubtype[]> {
    const snapshot = await getDocs(collection(this.firestore, this.collectionPath).withConverter(this.converter));
    return snapshot.docs
      .map((item) => item.data())
      .filter((item) => !options.enabledOnly || item.enabled)
      .sort(sortBlockSubtypes);
  }

  async get(id: string): Promise<BlockSubtype | null> {
    const snapshot = await getDoc(doc(this.firestore, this.collectionPath, id).withConverter(this.converter));
    return snapshot.exists() ? snapshot.data() : null;
  }

  async create(input: BlockSubtypeInput, actorId?: string | null): Promise<BlockSubtype> {
    const normalized = normalizeBlockSubtypeInput(input);
    const existing = await this.list();
    if (existing.length >= BLOCK_SUBTYPE_LIMIT) {
      throw new BlockSubtypeServiceError(
        'limit_exceeded',
        `A project may contain at most ${BLOCK_SUBTYPE_LIMIT} block subtypes.`,
      );
    }
    this.assertUniqueSlug(normalized.slug, existing);

    const ref = doc(collection(this.firestore, this.collectionPath)).withConverter(this.converter);
    const now = new Date();
    const resolvedActor = actorId ?? CoreFirebaseService.getInstance().getCurrentUserId() ?? null;
    const record: BlockSubtype = {
      id: ref.id,
      ...normalized,
      usageCount: 0,
      createdBy: resolvedActor,
      createdAt: now,
      updatedBy: resolvedActor,
      updatedAt: now,
    };
    await setDoc(ref, record);
    return record;
  }

  async update(
    id: string,
    patch: Partial<BlockSubtypeInput>,
    actorId?: string | null,
  ): Promise<BlockSubtype> {
    const existing = await this.get(id);
    if (!existing) throw new BlockSubtypeServiceError('not_found', `Block subtype ${id} was not found.`);
    const normalized = normalizeBlockSubtypeInput({ ...existing, ...patch });
    const catalog = await this.list();
    this.assertUniqueSlug(normalized.slug, catalog, id);

    const updated: BlockSubtype = {
      ...existing,
      ...normalized,
      updatedBy: actorId ?? CoreFirebaseService.getInstance().getCurrentUserId() ?? null,
      updatedAt: new Date(),
    };
    const ref = doc(this.firestore, this.collectionPath, id).withConverter(this.converter);
    await setDoc(ref, updated, { merge: true });
    return updated;
  }

  async delete(id: string): Promise<void> {
    const existing = await this.get(id);
    if (!existing) throw new BlockSubtypeServiceError('not_found', `Block subtype ${id} was not found.`);
    await deleteDoc(doc(this.firestore, this.collectionPath, id));
  }

  onCollectionSnapshot(
    onData: (subtypes: BlockSubtype[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const ref = collection(this.firestore, this.collectionPath).withConverter(this.converter);
    return onSnapshot(ref, (snapshot) => {
      onData(snapshot.docs.map((item) => item.data()).sort(sortBlockSubtypes));
    }, (error) => onError?.(error));
  }

  private assertUniqueSlug(slug: string, catalog: BlockSubtype[], ignoredId?: string): void {
    if (catalog.some((item) => item.id !== ignoredId && item.slug === slug)) {
      throw new BlockSubtypeServiceError(
        'duplicate_slug',
        `Block subtype slug "${slug}" already exists in this project.`,
      );
    }
  }
}

function sortBlockSubtypes(left: BlockSubtype, right: BlockSubtype): number {
  return left.name.localeCompare(right.name) || left.slug.localeCompare(right.slug) || left.id.localeCompare(right.id);
}

export { BlockSubtypeValidationError };
